<template>
  <div class="container mt-4">

    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 p-1">
      <h2 class="mb-0 main-title">Companies</h2>
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-md btn-primary fs-11 fw-normal hover-white" @click="openCreateModal('createModal')">
          <i class="fa fa-plus fs-11"></i> Add New Company
        </button>
      </div>
    </div>

    <!-- Filters Bar -->
    <div class="filters-wrapper">
      <div class="filters-bar" id="companyFiltersBar"></div>
      <button @click="resetAllFilters" class="btn btn-sm btn-outline-danger hover-white">
        <span class="material-symbols-outlined fs-6">undo</span> Reset
      </button>
    </div>

    <!-- Dropdown Template -->
    <div id="genericFilterDropdownTemplate" style="display: none;">
      <div class="generic-filter-dropdown">
        <div class="dropdown-header">
          <input type="text" class="filter-search-input" placeholder="Search...">
        </div>
        <div class="dropdown-body filter-options-container"></div>
        <div class="dropdown-footer">
          <button class="filter-clear-btn">Clear</button>
          <button class="filter-apply-btn">Apply</button>
        </div>
      </div>
    </div>

    <div class="card p-1 bg-white">
      <FormModal ref="createModal" title="Create New Company" id="create_company_form_modal">
        <CreateCompany ref="createCompanyForm" @created="onCompanyCreated" @close="closeCreateModal('createModal')" />
      </FormModal>
      <FormModal v-if="showEditModal" ref="editModal" title="Edit Company" id="edit_company_form_modal">
        <EditCompany :company-id="editingCompanyId" @updated="onCompanyUpdated" @close="closeEditModal" />
      </FormModal>
      <LoadingBar v-if="loading" message="Loading Companies..." />

      <!-- Table -->
      <div v-else class="table-wrapper">
        <ListTable>
          <colgroup>
            <col style="width: 28%">
            <col style="width: 25%">
            <col style="width: 17%">
            <col style="width: 14%">
            <col style="width: 10%">
            <col style="width: 6%">
          </colgroup>

          <thead>
            <tr>
              <th @click="sortBy('name')" class="sortable">
                Company <i :class="getSortIcon('name')"></i>
              </th>
              <th @click="sortBy('email')" class="sortable">
                Contact <i :class="getSortIcon('email')"></i>
              </th>

              <th>Status</th>
              <th @click="sortBy('address')" class="sortable">
                Address <i :class="getSortIcon('address')"></i>
              </th>
              <th @click="sortBy('created_at')" class="sortable">
                Created On <i :class="getSortIcon('created_at')"></i>
              </th>
              <th class="text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="company in sortedData" :key="company.external_id">
              <!-- Company name + logo -->
              <td class="align-middle">
                <div class="d-flex align-items-center gap-2" style="cursor: pointer;" @click="viewProfile(company)">
                  <div
                    class="d-flex align-items-center justify-content-center bg-primary-subtle border border-primary-subtle rounded-3 overflow-hidden"
                    style="width: 40px; height: 40px; min-width: 40px;">
                    <img v-if="company.logo" :src="getFileUrl(company.logo)" alt="Company Logo"
                      style="width: 100%; height: 100%; object-fit: cover;" />
                    <span v-else class="material-symbols-outlined text-primary fs-10">
                      business
                    </span>
                  </div>

                  <div>
                    <div class="mb-0 text-dark fw-500">{{ company.name }}</div>
                    <small class="text-muted">{{ company.external_id }}</small>
                  </div>
                </div>
              </td>
              <!-- Contact -->
              <td class="align-middle">
                <div>{{ company.email || '—' }}</div>
                <small class="text-muted">{{ company.phone || '' }}</small>
              </td>

              <!-- Status -->
              <td class="align-middle">
                <span class="badge" :class="statusClass(company.status)">
                  {{ formatLabel(company.status) || '—' }}
                </span>
              </td>

              <!-- Address -->
              <td class="align-middle">{{ company.address || '—' }}
              </td>

              <!-- Created -->
              <td class="align-middle">{{ company.created_at }}
              </td>

              <!-- Actions -->
              <td class="text-center align-middle">
                <TableActionDropdown>
                  <li>
                    <a class="dropdown-item" @click.prevent="viewProfile(company)">
                      <i class="fa fa-eye text-info"></i>
                      <span>Profile</span>
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item" @click.prevent="edit(company)">
                      <i class="fa fa-pencil text-primary"></i>
                      <span>Edit</span>
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item item-danger" @click.prevent="deleteCompany(company)">
                      <i class="fa fa-trash"></i>
                      <span>Delete</span>
                    </a>
                  </li>
                </TableActionDropdown>
              </td>
            </tr>

            <tr v-if="sortedData.length === 0">
              <td colspan="7" class="text-center py-4 text-muted">No Companies Found</td>
            </tr>
          </tbody>
        </ListTable>
      </div>

      <!-- Pagination -->
      <Pagination :pagination="pagination" @page-change="changePage" />

    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import ListTable from '@/components/utils/ListTable.vue'
import TableActionDropdown from '@/components/TableActionDropdown.vue'
import CreateCompany from '@/views/Company/CreateCompany.vue'
import EditCompany    from '@/views/Company/EditCompany.vue'

export default {
  name: 'CompanyList',
  components: {
    Pagination,
    LoadingBar,
    FormModal,
    ListTable,
    TableActionDropdown,
    CreateCompany,
    EditCompany,
  },

  data() {
    return {
      companies: [],
      typeOptions: [],
      statusOptions: [],
      pagination: {
        total_rows: 0,
        per_page: 20,
        current_page: 1,
        total_pages: 1,
      },
      loading: false,
      filterManager: null,
      sortColumn: null,
      sortOrder: 'asc',
      filteredData: [],
      currentFilters: {},
      editingCompanyId: null,
      showEditModal: false,
    }
  },

  computed: {
    sortedData() {
      let result = [...this.filteredData]

      if (this.sortColumn) {
        result.sort((a, b) => {
          let valA = a[this.sortColumn] ?? ''
          let valB = b[this.sortColumn] ?? ''

          if (typeof valA === 'string') valA = valA.toLowerCase()
          if (typeof valB === 'string') valB = valB.toLowerCase()

          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1
          return 0
        })
      }

      return result
    },
  },

  mounted() {
    this.fetchList()
  },

  methods: {

    // ── Modals ─────────────────────────────────────────────
    openCreateModal(ref) {
      this.$refs[ref].showModal()
    },

    closeCreateModal(ref) {
      this.$refs[ref].closeModal()
    },

    async onCompanyCreated() {
      this.pagination.current_page = 1
      await this.fetchList()
    },

    async onCompanyUpdated() {
      await this.fetchList()
    },

    edit(company) {
      this.editingCompanyId = company.external_id
      this.showEditModal = true
      this.$nextTick(() => {
        this.$refs.editModal.showModal()
      })
    },

    closeEditModal() {
      if (this.$refs.editModal) {
        this.$refs.editModal.closeModal()
      }
      this.editingCompanyId = null
      this.showEditModal = false
    },
  viewProfile(company) {
  if (!company?.external_id) {
    console.error('viewProfile: missing external_id', company)
    return
  }
  this.$router.push({
    name: 'CompanyProfile',
    params: { external_id: company.external_id },
  })
},
    // ── Fetch ──────────────────────────────────────────────
    async fetchList() {
      this.loading = true;

      const result = await form_api_submit({
        url: 'companies/list',
        method: 'GET',
        data: {
          page: this.pagination.current_page,
          ...this.currentFilters,
        },
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false,
      });

      if (result.success) {
        this.companies = result.data.list || [];
        this.filteredData = [...this.companies];
        this.pagination = result.data.list?.pagination || this.pagination;

        // Store dropdown options returned by API
        this.typeOptions = result.data.type_options || [];
        this.statusOptions = result.data.status_options || [];

        // Init filters once after first load
        if (!this.filterManager) {
          this.$nextTick(() => {
            setTimeout(() => this.initFilters(), 100);
          });
        }
      }

      this.loading = false;
    },
    async deleteCompany(company) {
      if (!confirm(`Delete "${company.name}"? This cannot be undone.`)) return

      const result = await form_api_submit({
        url: `companies/${company.external_id}/delete`,
        method: 'DELETE',
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: false,
      })

      if (result.success) {
        this.pagination.current_page = 1
        await this.fetchList()
      }
    },

    // ── Pagination ─────────────────────────────────────────
    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return
      this.pagination.current_page = page
      this.fetchList()
    },

    // ── Filters ────────────────────────────────────────────
    initFilters() {
      if (typeof $ === 'undefined' || typeof GenericFilterManager === 'undefined') return;

      // Static status options
      const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ];

      this.filterManager = new GenericFilterManager({
        containerId: 'companyFiltersBar',
        filters: [
          {
            key: 'name',
            label: 'Name',
            type: 'input',
            placeholder: 'Search by company name',
          },
          {
            key: 'email',
            label: 'Email',
            type: 'input',
            placeholder: 'Search by email',
          },
          {
            key: 'external_id',
            label: 'ID',
            type: 'input',
            placeholder: 'Search by company ID',
          },
          {
            key: 'status',
            label: 'Status',
            type: 'checkbox',
            options: statusOptions,
          },
        ],
        onFilterChange: (filters) => this.applyFilters(filters),
      });
    },

    applyFilters(activeFilters) {
      // Create a clean copy of filters
      const formattedFilters = {};

      // Handle text inputs (name, email, external_id)
      if (activeFilters.name && activeFilters.name.trim() !== '') {
        formattedFilters.name = activeFilters.name.trim();
      }

      if (activeFilters.email && activeFilters.email.trim() !== '') {
        formattedFilters.email = activeFilters.email.trim();
      }

      if (activeFilters.external_id && activeFilters.external_id.trim() !== '') {
        formattedFilters.external_id = activeFilters.external_id.trim();
      }

      // Handle status (convert array to comma-separated string)
      if (activeFilters.status) {
        let statusString = '';

        if (Array.isArray(activeFilters.status)) {
          // Filter out empty values and join
          statusString = activeFilters.status
            .filter(s => s && s.trim() !== '')
            .join(',');
        } else if (typeof activeFilters.status === 'string') {
          statusString = activeFilters.status;
        }

        if (statusString) {
          formattedFilters.status = statusString;
        }
      }

      console.log('Applied Filters:', formattedFilters);

      this.currentFilters = formattedFilters;
      this.pagination.current_page = 1;
      this.fetchList();
    },

    resetAllFilters() {
      if (this.filterManager) {
        this.filterManager.clearAllFilters();
      }
      this.currentFilters = {};
      this.sortColumn = null;
      this.sortOrder = 'asc';
      this.pagination.current_page = 1;
      this.fetchList();
    },
    // ── Sorting ────────────────────────────────────────────
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
      this.pagination.current_page = 1
      this.fetchList()
    },
  getFileUrl(filePath) {
      if (!filePath) return ''
      if (filePath.startsWith('http')) return filePath

      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${baseUrl}/${filePath}`
    },
    getSortIcon(column) {
      if (this.sortColumn !== column) return 'fas fa-sort text-muted'
      return this.sortOrder === 'asc'
        ? 'fas fa-sort-up text-primary'
        : 'fas fa-sort-down text-primary'
    },

    // ── Display Helpers ────────────────────────────────────
    formatLabel(str) {
      if (!str) return '—'
      return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },

    statusClass(status) {
      const map = {
        active: 'bg-success text-white',
        inactive: 'bg-secondary text-white',
        pending: 'bg-warning text-dark',
        suspended: 'bg-danger text-white',
      }
      return map[(status || '').toLowerCase()] ?? 'bg-light text-dark'
    },

    typeColor(type) {
      const palette = ['#EEEDFE', '#E1F5EE', '#FCEBEB', '#FAEEDA', '#E6F1FB', '#FBEAF0', '#EAF3DE', '#F1EFE8']
      const idx = Math.abs([...(type ?? '')].reduce((a, c) => a + c.charCodeAt(0), 0)) % palette.length
      return palette[idx]
    },
  },
}
</script>

<style scoped>
.page-link {
  cursor: pointer;
}

.page-item.active .page-link {
  background-color: #667eea;
  border-color: #667eea;
}

.fw-500 {
  font-weight: 500;
}
</style>